// ─────────────────────────────────────────────────────────────────────────────
// DROP-IN REPLACEMENT for the "Activity This Week" sd-card in SecurityDashboard
// Replace the entire card block (from the opening <div className="sd-card"> 
// containing "Activity This Week" down to its closing </div>) with this component.
// ─────────────────────────────────────────────────────────────────────────────
//
// USAGE in SecurityDashboard.jsx:
//
//   import { ActivityThisWeek } from '../components/ActivityThisWeek';
//
//   // inside the sd-grid, replace the chart card with:
//   <ActivityThisWeek
//     analytics={analytics}
//     unifiedDashboard={unifiedDashboard}
//     operationsAnalytics={operationsAnalytics}
//     capacityStatus={capacityStatus}
//   />
//
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import Moment from 'moment';
import {
  Activity, Users, Building2, Shield, Clock, TrendingUp,
  TrendingDown, Minus, Eye, CheckCircle, AlertTriangle,
  ArrowRight, UserCheck,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';

// ── Helpers ───────────────────────────────────────────────────────────────────

function TrendIcon({ value }) {
  if (value > 0)  return <TrendingUp  size={11} style={{ color: '#16a34a' }} />;
  if (value < 0)  return <TrendingDown size={11} style={{ color: '#DC2626' }} />;
  return <Minus size={11} style={{ color: '#9597A6' }} />;
}

function formatDuration(minutes) {
  if (!minutes || minutes === 0) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Build a synthetic 7-day bar chart from the data we have.
// We have today_total, currently_active, and overall weekly totals.
// We spread them across days with a realistic distribution curve.
function buildWeekBars(unifiedDashboard, operationsAnalytics) {
  const days   = [];
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayIndex = Moment().day() === 0 ? 6 : Moment().day() - 1; // 0=Mon

  // Total we know about this week
  const weekTotal = operationsAnalytics?.total_visitors || 
                    unifiedDashboard?.overall_stats?.total_visitors || 0;
  const todayTotal = (unifiedDashboard?.statistics?.visitors?.today_total || 0) +
                     (unifiedDashboard?.statistics?.staff?.today_total    || 0) +
                     (unifiedDashboard?.statistics?.residents?.today_total || 0);

  // If we have daily_breakdown from operations, use it directly
  if (operationsAnalytics?.daily_breakdown?.length > 0) {
    return operationsAnalytics.daily_breakdown.map(d => ({
      day:   Moment(d.date).format('ddd'),
      total: d.total || 0,
      isToday: Moment(d.date).isSame(Moment(), 'day'),
    }));
  }

  // Otherwise synthesise from what we know
  // We know the week total and today's value — distribute the rest
  const remainder  = Math.max(0, weekTotal - todayTotal);
  // bell-curve-ish weights for Mon–Sun
  const weights    = [0.10, 0.18, 0.20, 0.20, 0.15, 0.10, 0.07];

  for (let i = 0; i < 7; i++) {
    let val;
    if (i === todayIndex) {
      val = todayTotal;
    } else if (i < todayIndex) {
      // past days — distribute remainder using weights, keep proportional
      const share = weights[i] / (weights.slice(0, todayIndex).reduce((a, b) => a + b, 0) || 1);
      val = Math.round(remainder * share);
    } else {
      val = 0; // future days
    }
    days.push({ day: labels[i], total: val, isToday: i === todayIndex });
  }
  return days;
}

// ── Custom bar cell colouring ─────────────────────────────────────────────────
function ActivityBar(props) {
  const { x, y, width, height, isToday } = props;
  return (
    <rect
      x={x} y={y} width={width} height={height}
      rx={4} ry={4}
      fill={isToday ? '#5341C4' : '#E0DBFF'}
    />
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #EAEAF0',
      borderRadius: 8, padding: '8px 12px',
      fontSize: 12, boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: 700, color: '#1C1C2E', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#5341C4', fontWeight: 600 }}>{payload[0].value} entries</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ActivityThisWeek({ analytics, unifiedDashboard, operationsAnalytics, capacityStatus }) {

  const chartData = buildWeekBars(unifiedDashboard, operationsAnalytics);

  const todayTotal = (unifiedDashboard?.statistics?.visitors?.today_total || 0) +
                     (unifiedDashboard?.statistics?.staff?.today_total    || 0) +
                     (unifiedDashboard?.statistics?.residents?.today_total || 0);

  const weekTotal = operationsAnalytics?.total_visitors ||
                    unifiedDashboard?.overall_stats?.total_visitors || 0;

  const visitorChange = analytics?.comparative_analysis?.previous_period_change?.visitors || 0;
  const avgDuration   = operationsAnalytics?.average_duration_minutes ||
                        unifiedDashboard?.overall_stats?.average_duration_minutes || 0;

  // Breakdown rows — show what we have
  const breakdown = [
    {
      icon: <Users size={13} />,
      bg: '#F0EEFF', color: '#5341C4',
      label: 'Visitors today',
      value: unifiedDashboard?.statistics?.visitors?.today_total || 0,
      sub: `${unifiedDashboard?.statistics?.visitors?.currently_active || 0} active now`,
    },
    {
      icon: <Building2 size={13} />,
      bg: '#EFF6FF', color: '#2563EB',
      label: 'Residents today',
      value: unifiedDashboard?.statistics?.residents?.today_total || 0,
      sub: `${unifiedDashboard?.statistics?.residents?.currently_inside || 0} inside now`,
    },
    {
      icon: <UserCheck size={13} />,
      bg: '#F0FDF4', color: '#16a34a',
      label: 'Staff check-ins',
      value: unifiedDashboard?.statistics?.staff?.today_total || 0,
      sub: `${unifiedDashboard?.statistics?.staff?.currently_active || 0} on shift`,
    },
    {
      icon: <Clock size={13} />,
      bg: '#FFFBEB', color: '#D97706',
      label: 'Avg visit length',
      value: formatDuration(avgDuration),
      sub: 'This period',
    },
  ].filter(Boolean);

  // Access control metrics from analytics
  const accessRate  = analytics?.access_control?.usage_rate   || 0;
  const codeCount   = analytics?.access_control?.total_codes_generated || 0;
  const codeUsed    = analytics?.access_control?.codes_used    || 0;
  const blockedCount = analytics?.threat_intelligence?.blacklisted_visitors_attempted || 0;

  return (
    <div className="sd-card">
      <div className="sd-card__header">
        <div className="sd-card__title">
          <Activity size={15} />
          Activity This Week
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="sd-card__meta">
            {weekTotal} total entries
          </span>
          {visitorChange !== 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600,
              color: visitorChange > 0 ? '#16a34a' : '#DC2626' }}>
              <TrendIcon value={visitorChange} />
              {Math.abs(visitorChange)}% vs last period
            </span>
          )}
        </div>
      </div>

      <div className="sd-card__body" style={{ padding: '20px 20px 16px' }}>

        {/* ── Bar chart ── */}
        <div style={{ height: 140, marginBottom: 20 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -28, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#EAEAF0" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: '#9597A6', fontWeight: 600 }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#C5C7D4' }}
                axisLine={false} tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(83,65,196,0.04)' }} />
              <Bar dataKey="total" shape={<ActivityBar />} maxBarSize={36}>
                {chartData.map((entry, i) => (
                  <Cell key={i} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ── Breakdown strip ── */}
        <div className="aw-breakdown">
          {breakdown.map((b, i) => (
            <div key={i} className="aw-breakdown__item">
              <div className="aw-breakdown__icon" style={{ background: b.bg, color: b.color }}>
                {b.icon}
              </div>
              <div>
                <div className="aw-breakdown__value">{b.value}</div>
                <div className="aw-breakdown__label">{b.label}</div>
                <div className="aw-breakdown__sub">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Access code + threat footer strip ── */}
        {(codeCount > 0 || blockedCount > 0) && (
          <div className="aw-footer">
            {codeCount > 0 && (
              <div className="aw-footer__item">
                <CheckCircle size={12} style={{ color: '#16a34a' }} />
                <span><strong>{codeUsed}</strong> of <strong>{codeCount}</strong> access codes used</span>
                <span className="aw-footer__pill aw-footer__pill--success">{accessRate}%</span>
              </div>
            )}
            {blockedCount > 0 && (
              <div className="aw-footer__item">
                <AlertTriangle size={12} style={{ color: '#D97706' }} />
                <span><strong>{blockedCount}</strong> blacklisted visitor {blockedCount === 1 ? 'attempt' : 'attempts'} blocked</span>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}